/**
 * Canonical contracts for the document-parsing IR.
 * Parsers produce these; consumers hydrate them.
 */

import { Schema } from 'effect';

const mutable = <S extends Schema.Constraint>(schema: S): Schema.mutableKey<S> =>
  Schema.mutableKey(schema);
const optionalMutable = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutableKey(Schema.optionalKey(schema));
const mutableArray = <S extends Schema.Constraint>(schema: S) =>
  Schema.mutable(Schema.Array(schema));

const NormalizedNumber = Schema.Finite.check(Schema.isBetween({ minimum: 0, maximum: 1 }));

// ── Canonical Bbox ─────────────────────────────────
// ALL coordinates 0-1 relative. Parsers MUST normalize to this.

export const CanonicalBboxSchema = Schema.Struct({
  x: mutable(NormalizedNumber),
  y: mutable(NormalizedNumber),
  w: mutable(NormalizedNumber),
  h: mutable(NormalizedNumber),
});

export type CanonicalBbox = typeof CanonicalBboxSchema.Type;

// ── Canonical Block (the IR that hydration consumes) ───────────────

/** Canonical block types used at the export/render boundary. */
export const BlockTypeSchema = Schema.Literals([
  'table',
  'row',
  'cell',
  'text',
  'figure',
  'header',
  'footer',
  'key_value',
  'list',
]);

export type BlockType = typeof BlockTypeSchema.Type;

export interface CanonicalBlock {
  /** Raw parser-specific type — preserved as-is for fidelity. Use resolveBlockType() at the export/render boundary. */
  type: string;
  label?: string;
  value?: string;
  bbox?: CanonicalBbox;
  confidence?: number;
  children?: CanonicalBlock[];
}

export const CanonicalBlockSchema: Schema.Codec<CanonicalBlock> = Schema.Struct({
  type: mutable(Schema.String),
  label: optionalMutable(Schema.String),
  value: optionalMutable(Schema.String),
  bbox: optionalMutable(CanonicalBboxSchema),
  confidence: optionalMutable(NormalizedNumber),
  children: optionalMutable(
    mutableArray(Schema.suspend((): Schema.Codec<CanonicalBlock> => CanonicalBlockSchema)),
  ),
});

export const CanonicalPageSchema = Schema.Struct({
  pageNumber: mutable(Schema.Finite),
  width: optionalMutable(Schema.Finite),
  height: optionalMutable(Schema.Finite),
  blocks: mutable(mutableArray(CanonicalBlockSchema)),
});

export type CanonicalPage = typeof CanonicalPageSchema.Type;

// ── Canonical Element (entity metadata) ────────────────────────────

export const CanonicalElementSchema = Schema.Struct({
  title: mutable(Schema.String),
  bbox: mutable(CanonicalBboxSchema),
  schema: optionalMutable(mutableArray(Schema.String)),
  is_complete: optionalMutable(Schema.Boolean),
});

export type CanonicalElement = typeof CanonicalElementSchema.Type;

export const CanonicalEntityMetadataSchema = Schema.Struct({
  tables: mutable(mutableArray(CanonicalElementSchema)),
  figures: mutable(mutableArray(CanonicalElementSchema)),
  footnotes: mutable(mutableArray(CanonicalElementSchema)),
  signatures: mutable(mutableArray(CanonicalElementSchema)),
});

export type CanonicalEntityMetadata = typeof CanonicalEntityMetadataSchema.Type;

// ── Canonical Parse Result ─────────────────────────────────

export interface CanonicalParserUsage {
  provider?: string;
  model?: string;
  actualModelId?: string;
  parserProfile?: string;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  costUsd?: number;
  costEstimated?: boolean;
  currency?: string;
}

export const CanonicalParserUsageSchema: Schema.Codec<CanonicalParserUsage> = Schema.Struct({
  provider: optionalMutable(Schema.String),
  model: optionalMutable(Schema.String),
  actualModelId: optionalMutable(Schema.String),
  parserProfile: optionalMutable(Schema.String),
  inputTokens: optionalMutable(Schema.Finite),
  outputTokens: optionalMutable(Schema.Finite),
  totalTokens: optionalMutable(Schema.Finite),
  costUsd: optionalMutable(Schema.Finite),
  costEstimated: optionalMutable(Schema.Boolean),
  currency: optionalMutable(Schema.String),
});

export const CanonicalParseResultSchema = Schema.Struct({
  pages: mutable(mutableArray(CanonicalPageSchema)),
  /** Parser provenance retained for lossless derived projections. */
  rawPages: optionalMutable(mutableArray(Schema.Unknown)),
  metadata: mutable(
    Schema.Struct({
      /** The parser id that produced this result. */
      parser: optionalMutable(Schema.String),
      /** Legacy server alias retained so existing `/v1` payloads stay unchanged. */
      vendor: optionalMutable(Schema.String),
      model: optionalMutable(Schema.String),
      durationMs: mutable(Schema.Finite),
      confidence: mutable(Schema.NullOr(NormalizedNumber)),
      pageCount: mutable(Schema.Finite),
      usage: optionalMutable(CanonicalParserUsageSchema),
    }),
  ),
});

export type CanonicalParseResult = typeof CanonicalParseResultSchema.Type;
